import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  delay, find, indexOf, values, keyBy, first,
  reduce, each, findIndex, filter, map, difference,
} from 'lodash';
import Konami from 'react-konami';
import moment from 'moment';
import Joi from 'joi';
import ReactResizeDetector from 'react-resize-detector';
import Fa from 'react-fontawesome';
import { Modal, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import ProgressBar from '../../../../components/progress-bar';
import SearchField from '../../../../components/search-field';
import Typinging from '../../../../components/typing';
import DateTimeDisplay from '../../../../components/date-time-display';
import SellerFeedbackForm from '../../../Feedback/components/seller-feedback-modal/feedback-form';
import { transalteLanguageCode, transalteLanguageCodes } from '../../../../util/helpers';

import {
  requestEditWinLossOpportunityFeedbacks,
  requestEditWinLossAccountFeedbacks,
  fetchWinLossOpportunityContacts,
  fetchWinLossAccountContacts,
  submitContactsToSurveyCollector,
  fetchWinLossOpportunitySurveys,
  fetchWinLossAccountSurveys,
  winLossCancelSurveys,
} from '../../DashboardActions';
import {
  getWinLossEditingOpportunity, getWinLossEditingAccount,
  getWinLossOpportunitySurveys,
  getFeedbackRequestSettings,
  getFeedbackRequestStatus,
} from '../../DashboardReducer';
import ContactEditor from './ContactEditor';
import InitiativeSelector from './InitiativeSelector';
import NumberDisplay from '../../../../components/number-display';
import {
  getTVProductClients,
  getTVCollectorMappings,
  getTVCollectorRefs,
  getTVProductDictionary,
  getTVUseProductMappings,
  getTVRequireProducts,
  getTVAllowMultipleProducts,
} from '../../../Setup/SetupReducer';
import { getIsMobile, getViewableDimensions } from '../../../App/AppReducer';
import { getUser, getUserCurrentOrganization, getCollectorLanguageMappings } from '../../../Auth/AuthReducer';
import RequiredSignature from '../../../../components/request-signature';
import OwnerSelector from './OwnerSelector';
import CheckUserEmailSignature from '../../../../components/request-signature/CheckUserEmailSignature';
import CollectorStepSummary from './CollectorStepSummary';
import AddContactButton from '../../../../components/add-opportunity-contact';
import RequiredUserEmailSignature from '../../../../components/request-signature/RequireUserEmailSignature';
import ManagerList from '../../../../components/required-setup-mappings/managers';
import ConfirmationModal from './modalConfirmation';

import styles from './styles.css';

// Legacy constraints for reference:
// const constraints = {
//   first_name: { presence: true, length: { maximum: 200, minimum: 1 } },
//   last_name: { presence: true, length: { maximum: 200, minimum: 1 } },
//   title: { length: { maximum: 200 } },
//   email: { presence: true, email: true, length: { maximum: 200 } },
//   phone: { length: { maximum: 30 } },
//   phone_ext: { length: { maximum: 10 } },
//   mobile_phone: { length: { maximum: 20 } },
//   node: { length: { maximum: 2048 } },
// };

const contactSchema = Joi.object({
  first_name: Joi.string().min(1).max(200).required().messages({
    'string.base': 'First name must be a string',
    'string.empty': 'First name is required',
    'any.required': 'First name is required',
    'string.min': 'First name is not allowed to be empty',
    'string.max': 'First name must be at most 200 characters',
  }),
  last_name: Joi.string().min(1).max(200).required().messages({
    'string.base': 'Last name must be a string',
    'string.empty': 'Last name is required',
    'any.required': 'Last name is not allowed to be empty',
    'string.min': 'Last name is not allowed to be empty',
    'string.max': 'Last name must be at most 200 characters',
  }),
  title: Joi.string().max(200).allow('', null).messages({
    'string.base': 'Title must be a string',
    'string.max': 'Title must be at most 200 characters',
  }),
  email: Joi.string().email({ tlds: { allow: false } }).max(200).required().messages({
    'string.base': 'Email must be a string',
    'string.empty': 'Email is not allowed to be empty',
    'any.required': 'Email is not allowed to be empty',
    'string.email': 'Email must be a valid email address',
    'string.max': 'Email must be at most 200 characters',
  }),
  phone: Joi.string().max(30).allow('', null).messages({
    'string.max': 'Phone must be at most 30 characters',
  }),
  phone_ext: Joi.string().max(10).allow('', null).messages({
    'string.max': 'Phone extension must be at most 10 characters',
  }),
  mobile_phone: Joi.string().max(20).allow('', null).messages({
    'string.max': 'Mobile phone must be at most 20 characters',
  }),
  notes: Joi.string().max(2048).allow('', null).messages({
    'string.max': 'Notes must be at most 2048 characters',
  }),
}).unknown(true);

const validateContact = (contact) => {
  const { error } = contactSchema.validate(contact, { abortEarly: false });
  return error ? error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {}) : undefined;
};

const PROGRAM_TYPES = ['Win Loss', 'Voice of the Buyer', 'Customer Experience'];

const MODE_BUYER = 'buyer';
const MODE_SELLER = 'seller';

function GroupRowInnerRenderer({ value }) {
  let text = 'Contacts';
  if (value === 'OPPORTUNITY') {
    text = 'Opportunity Contacts';
  } else if (value === 'ACCOUNT') {
    text = 'Account Contacts';
  } else if (value === 'ACCOUNT_RELATION') {
    text = 'Account Relation Contacts';
  }
  return (
    <strong>{text}</strong>
  );
}

function initProductList(props) {
  const {
    tvClients, tvCollectors, collectors, object, tvProductMappings,
    collectorTypes,
    tvUseProductMappings,
    feedbackRequestSettings,
    withPreferCollector,
    allowMultipleProducts,
  } = props;

  const isAccount = object && !object.opportunity_id && object.account_id;

  const programs = reduce(tvClients, (items, client) => {
    each(client.programs, (program) => {
      if (indexOf(PROGRAM_TYPES, program.type_name) >= 0) {
        const clientCollectors = find(tvCollectors, { id: client.id });
        let opportunityProducts = [];
        const programCollectors = [];
        if (clientCollectors) {
          const clientProgram = find(clientCollectors.programs, { id: program.id });
          if (clientProgram) {
            if (collectors[client.id] && collectors[client.id][program.id]) {
              each(collectorTypes, (type) => {
                if (clientProgram[`${type}_collector_ids`]) {
                  each(clientProgram[`${type}_collector_ids`], (cid) => {
                    const col = find(collectors[client.id][program.id], { collector_id: cid });
                    if (col) {
                      programCollectors.push({ ...col, type });
                    }
                  });
                }
              });
            }
          }
          if (programCollectors.length > 0 && object) {
            if (isAccount) {
              // select everything available
              opportunityProducts = reduce(tvProductMappings || [], (products, mappedProducts) => {
                each(mappedProducts, (mp) => {
                  if (mp.client_id === client.id && mp.program_id === program.id) {
                    products.push({
                      id: mp.id,
                      name: mp.name,
                      definition: mp.definition,
                    });
                  }
                });
                return products;
              }, []);
              opportunityProducts = values(keyBy(opportunityProducts, 'id'));
            } else {
              opportunityProducts = reduce(object.line_items || [], (products, lineItem) => {
                const mappedProducts = tvProductMappings[lineItem.product_id] || [];
                each(mappedProducts, (mp) => {
                  if (mp.client_id === client.id && mp.program_id === program.id) {
                    products.push({
                      id: mp.id,
                      name: mp.name,
                      definition: mp.definition,
                    });
                  }
                });
                return products;
              }, []);
            }
          }
        }
        // now, the product list we want to display for the drop down
        let hasAtLeastOneMappedProduct = false;
        let productList = [];

        if (!tvUseProductMappings) {
          // no products avaialbel for this account/opp
          // we select all
          productList = reduce(tvProductMappings.__ALL__ || [], (ps, p) => {
            if (p.program_id === program.id) {
              ps.push({
                ...p,
                selected: allowMultipleProducts,
              });  
            }
            return ps;
          }, []);
          hasAtLeastOneMappedProduct = productList.length > 0;
        } else {
          hasAtLeastOneMappedProduct = opportunityProducts.length > 0;
          productList = reduce(hasAtLeastOneMappedProduct ? opportunityProducts : program.products, (ps, p) => {
            ps.push({
              ...p,
              selected: hasAtLeastOneMappedProduct && allowMultipleProducts,
            });
            return ps;
          }, []);
        }

        const item = {
          ...program,
          selected: false,
          client_id: client.id,
          client_name: client.name,
          collectors: programCollectors,
          defaultCollector: null,
          opportunityProducts,
          hasAtLeastOneMappedProduct,
          productList,
        };
        if (programCollectors.length) {
          item.defaultCollector = first(programCollectors);

          if (!isAccount && feedbackRequestSettings.collector_id && withPreferCollector) {
            item.defaultCollector = find(programCollectors, { collector_id: feedbackRequestSettings.collector_id }) || first(programCollectors);
          }
          items.push(item);
        }
      }
    });
    return items;
  }, []);

  let preselectedProgram = null;
  // find program that has at least one mapped product according to this opportunity
  preselectedProgram = find(programs, p => p.defaultCollector !== null && p.opportunityProducts && p.opportunityProducts.length > 0);

  if (!preselectedProgram) {
    // or has a default collector
    preselectedProgram = find(programs, p => p.defaultCollector !== null);
  }
  if (preselectedProgram) {
    preselectedProgram.selected = true;
  }
  return programs;
}

GroupRowInnerRenderer.propTypes = {
  value: PropTypes.string,
};
GroupRowInnerRenderer.defaultProps = {
  value: null,
};

const ContactDetailRenderer = (props) => {
  const {
    value, currentSelectedContact,
    editedContacts, data,
    submitted, createdAt, status,
    statusUpdatedDate,
    doNotContact, canResubmit,
  } = props;
  const isActive = currentSelectedContact ? currentSelectedContact._id === data._id : false;
  const existend = find(editedContacts, { _id: data._id });
  let display = value;
  if (!display || display.length === '' || display.length === 0) {
    display = `${data.first_name} ${data.last_name}`;
  }
  let star = null;
  if (data.role && data.role.is_primary) {
    const tip = (
      <Tooltip id={`contact-${data._id}`}>Opportunity Primary Contact</Tooltip>
    );
    star = (
      <OverlayTrigger placement="right" overlay={tip}>
        <span>&nbsp;<Fa name="star" className="text-warning" /></span>
      </OverlayTrigger>
    );
  }
  let checked = null;

  const style = {
    fontSize: '1.5em', display: 'inline-block', position: 'relative', top: '3px',
  };
  if (submitted) {
    const dateAdded = createdAt ? <DateTimeDisplay value={createdAt} format="short" /> : null;
    const isTerminated = status === 'TERMINATED';
    const submmitedTip = (
      <Tooltip id={`contact-submited-${data._id}`}>
        <div>
          {isTerminated &&
            <div>
              {statusUpdatedDate === null && <span>Previously terminated</span>}
              {statusUpdatedDate !== null &&
                <span>
                  Previously terminated on <DateTimeDisplay value={statusUpdatedDate} format="short" />.
                </span>
              }
            </div>
          }
          {!isTerminated &&
            <div>
              {dateAdded === null && <span>Previously submitted</span>}
              {dateAdded !== null && <span>Previously submitted on {dateAdded}</span>}
            </div>
          }
        </div>
      </Tooltip>
    );
    checked = (
      <OverlayTrigger placement="right" overlay={submmitedTip}>
        <span>
          <Fa
            style={style}
            name={isTerminated ? 'ban' : 'check-circle'}
            className={isTerminated ? 'text-danger' : 'text-success'}
          />
          &nbsp;&nbsp;
        </span>
      </OverlayTrigger>
    );
  } else if (doNotContact) {
    const submmitedTip = (
      <Tooltip id={`contact-submited-${data._id}`}>
        <div>
          {display} has been set to &ldquo;Do Not Contact&rdquo; and cannot be submitted for feedback.
        </div>
      </Tooltip>
    );
    checked = (
      <OverlayTrigger placement="right" overlay={submmitedTip}>
        <span>
          <Fa
            style={style}
            name="times-circle"
            className="text-danger"
          />
          &nbsp;&nbsp;
        </span>
      </OverlayTrigger>
    );
  }

  const invalid = existend ? existend.$invalid === true : false;
  const error = invalid ? <span>&nbsp;<Fa name="exclamation-triangle" /></span> : null;
  const typing = isActive ? <span>&nbsp;<Typinging /></span> : null;
  let cls = '';
  if (invalid) {
    cls = 'text-danger';
  } else if (doNotContact) {
    cls = 'text-muted';
  }
  return (<span className={cls}>{checked}{display}{star}{error}{typing}</span>);
};
ContactDetailRenderer.propTypes = {
  value: PropTypes.string,
  currentSelectedContact: PropTypes.shape({
    _id: PropTypes.string,
  }),
  data: PropTypes.shape({
    _id: PropTypes.string,
  }),
  submitted: PropTypes.bool,
  createdAt: PropTypes.string,
  editedContacts: PropTypes.arrayOf(PropTypes.shape({})),
  doNotContact: PropTypes.bool,
  canResubmit: PropTypes.bool,
  status: PropTypes.string,
  statusUpdatedDate: PropTypes.string,
};
ContactDetailRenderer.defaultProps = {
  value: null,
  currentSelectedContact: null,
  data: null,
  editedContacts: [],
  doNotContact: false,
  submitted: false,
  createdAt: null,
  status: null,
  canResubmit: false,
  statusUpdatedDate: null,
};

const initState = {
  programs: [],
  currentSelectedContact: null,
  editedContacts: [],
  showingEditor: false,
  selectedContacts: [],
  valid: false,
  submitting: false,
  resultContacts: [],
  errorContacts: [],
  foundProduct: false,
  selectedOwner: null,
  forceSenderEmailFrom: false,
  submittedContacts: {},
  surveyLoading: true,
  surveyLoaded: false,
  surveyError: null,
  surveyErrorCode: null,
  mode: null,
  checkAutoSubmit: {},
  sellerFormSuccess: false,
  showConfirmationModal: false,
  debug: false,
};

export const OPPORTUNITY_COMPONENT_TYPE = 'OPPORTUNITY';
export const ACCOUNT_COMPONENT_TYPE = 'ACCOUNT';

export default function createComponent(componentType = OPPORTUNITY_COMPONENT_TYPE) {
  class RequestOpportunityFeedbacks extends Component {
    constructor() {
      super();
      this.state = {
        loading: false,
        loaded: false,
        errors: null,
        headerHeight: 0,
        footerHeight: 0,
        ...initState,
      };
      this.close = this.close.bind(this);
      this.closeWithNotification = this.closeWithNotification.bind(this);
      this.onGridReady = this.onGridReady.bind(this);
      this.onSelectionChanged = this.onSelectionChanged.bind(this);
      this.valueGetter = this.valueGetter.bind(this);
      this.handleSearch = this.handleSearch.bind(this);
      this.onContactCellClicked = this.onContactCellClicked.bind(this);
      this.handleContactUpdate = this.handleContactUpdate.bind(this);
      this.onContactRowSelected = this.onContactRowSelected.bind(this);
      this.handleProgramSelected = this.handleProgramSelected.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleResubmit = this.handleResubmit.bind(this);
      this.fixErrors = this.fixErrors.bind(this);
      this.handleSelectedOwner = this.handleSelectedOwner.bind(this);
      this.handleContactAdded = this.handleContactAdded.bind(this);
      this.handleProductSelected = this.handleProductSelected.bind(this);
      this.handleCollectorSelected = this.handleCollectorSelected.bind(this);
      this.checkboxSelection = this.checkboxSelection.bind(this);
      this.columnCellRendererSelector = this.columnCellRendererSelector.bind(this);
      this.handleHeaderResize = this.handleHeaderResize.bind(this);
      this.handleFooterResize = this.handleFooterResize.bind(this);
      this.handleSellerFeedbackSuccess = this.handleSellerFeedbackSuccess.bind(this);
      this.handleRequestTerminate = this.handleRequestTerminate.bind(this);
      this.getContextMenuItems = this.getContextMenuItems.bind(this);
      this.onCancelSuccess = this.onCancelSuccess.bind(this);
      this.onLanguageChangeSuccess = this.onLanguageChangeSuccess.bind(this);
      this.getRowClass = this.getRowClass.bind(this);
      this.checkAutoSubmit = this.checkAutoSubmit.bind(this);
      this.buildMessage = this.buildMessage.bind(this);
      this.toggleDebug = this.toggleDebug.bind(this);
    }

    componentWillMount() {
      const k = this.props.isAccount ? 'account_id' : 'opportunity_id';
      if (this.props.mode === MODE_BUYER && this.props.object && this.props.object[k]) {
        this.initData(this.props);
      }
    }

    componentDidMount() {
      this.mounted = true;
    }

    componentWillReceiveProps(nextProps) {
      if (nextProps.mode === MODE_BUYER && nextProps.object) {
        const k = nextProps.isAccount ? 'account_id' : 'opportunity_id';
        const currentObject = this.props.object || {};
        const shouldLoad = currentObject[k] !== nextProps.object[k] || this.props.mode !== nextProps.mode;
        if (shouldLoad) {
          this.initData(nextProps);
        }
      }
    }

    componentWillUnmount() {
      this.mounted = false;
    }

    toggleDebug() {
      this.setState({ debug: !this.state.debug });
    }

    checkAutoSubmit(key) {
      if (this.props.object && this.props.object.contact) {
        const fn = () => {
          const c = this.state.checkAutoSubmit || {};
          if (!c[key]) {
            this.setState({
              checkAutoSubmit: {
                ...c,
                [key]: true,
              },
            }, () => {
              delay(() => {
                if (this.state.valid) {
                  const r = this.handleSubmit();
                  if (r) {
                    // can submit
                    this.setState({ submitting: false });
                  } else {
                    this.setState({ submitting: true });
                  }
                }
              }, 100);
            });
          }
        }
        if (key === 'onGridReady') {
          this.handleContactAdded(this.props.object.contact, null, () => {
            fn();
          });
        } else {
          fn();
        }
      }
    }

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.gridApi.sizeColumnsToFit();
      const { selectedContacts } = this.state;
      const ids = map(selectedContacts, 'contact_id');
      this.gridApi.forEachNode((node) => {
        const nodeData = node.data;
        if (nodeData) {
          node.setSelected(indexOf(ids, nodeData.contact_id) >= 0);
        } else {
          node.setSelected(false);
        }
      });
      this.checkValid(() => {
        this.checkAutoSubmit('onGridReady');
      });
    }

    onSelectionChanged() {
      delay(() => {
        this.checkValid();
      }, 500);
    }

    onCancelSuccess(contact) {
      if (this.gridApi) {
        const node = this.gridApi.getRowNode(contact.contact_id);
        if (node) {
          this.setState({
            submittedContacts: {
              ...this.state.submittedContacts,
              [contact.contact_id]: {
                ...this.state.submittedContacts[contact.contact_id],
                ...contact,
              },
            },
          }, () => {
            this.gridApi.refreshCells({ rowNodes: [node], force: true });
            this.gridApi.flashCells({ rowNodes: [node] });
          });
        }
      }
    }

    onLanguageChangeSuccess(contact) {
      if (this.gridApi) {
        const node = this.gridApi.getRowNode(contact.contact_id);
        if (node) {
          this.setState({
            submittedContacts: {
              ...this.state.submittedContacts,
              [contact.contact_id]: {
                ...this.state.submittedContacts[contact.contact_id],
                ...contact,
              },
            },
          }, () => {
            this.gridApi.refreshCells({ rowNodes: [node], force: true });
            this.gridApi.flashCells({ rowNodes: [node] });
          });
        }
      }
    }

    onContactRowSelected({ node, data }) {
      if (node.selected) {
        const { editedContacts } = this.state;
        const idx = findIndex(editedContacts, { _id: data._id });
        const newEditedContacts = [...editedContacts];
        const contact = { ...data };
        contact.$invalid = validateContact(contact) !== undefined;
        if (idx >= 0) {
          newEditedContacts[idx] = contact;
        } else {
          newEditedContacts.push(contact);
        }
        this.setState({ editedContacts: newEditedContacts }, () => {
          if (!this.state.currentSelectedContact) {
            this.setState({ currentSelectedContact: contact });
          }
          this.checkValid();
        });
      } else if (this.state.currentSelectedContact && this.state.currentSelectedContact._id === data._id) {
        this.setState({ currentSelectedContact: null }, () => {
          this.checkValid();
        });
      }
      this.gridApi.sizeColumnsToFit();
    }

    onContactCellClicked(params, cb = () => {}) {
      const { node, data } = params;
      const { editedContacts } = this.state;
      const { valid, submittedContact, canResubmit } = this.getNodeInfo(data);

      if (!valid) {
        if (submittedContact && !canResubmit) {
          params.api.contextMenuFactory.showMenu(
            params.node,
            params.column,
            params.value,
            params.event,
          );
        }

        if (submittedContact) {
          if (!canResubmit) {
            node.setSelected(false);
            return;
          }
        } else {
          node.setSelected(false);
          return;
        }
        this.setState({ currentSelectedContact: submittedContact, showingEditor: true }, () => {
          this.gridApi.sizeColumnsToFit();
          this.checkValid();
        });

        if (!canResubmit || !submittedContact) {
          return;
        }
      }

      if (!this.gridApi) {
        return;
      }
      if (node.selected) {
        this.gridApi.forEachNode((n) => {
          if (n.data && n.data._id === data._id) {
            n.setSelected(!n.selected);
          }
        });
        const newEditedContacts = filter(editedContacts, (c) => {
          return c._id !== data._id;
        });
        let { showingEditor, currentSelectedContact } = this.state;
        if (currentSelectedContact && currentSelectedContact._id === data._id) {
          showingEditor = false;
          currentSelectedContact = null;
        }
        this.setState({ currentSelectedContact, editedContacts: newEditedContacts, showingEditor }, () => {
          this.gridApi.sizeColumnsToFit();
          this.checkValid();
        });
        return;
      }

      if (!node.selected) {
        this.gridApi.forEachNode((n) => {
          if (n.data && n.data._id === data._id) {
            n.setSelected(!n.selected);
          }
        });
      }
      const idx = findIndex(editedContacts, { _id: data._id });
      let newEditedContacts = editedContacts;
      let contact = data;
      if (idx >= 0) {
        contact = newEditedContacts[idx];
      } else {
        newEditedContacts = [...editedContacts];
        newEditedContacts.push(contact);
      }
      this.setState({ currentSelectedContact: contact, editedContacts: newEditedContacts, showingEditor: true }, () => {
        this.gridApi.sizeColumnsToFit();
        this.checkValid(cb);
      });
    }

    getRowClass(params) {
      if (!params || !params.data) {
        return null;
      }
      const { submittedContacts } = this.state;
      const survey = submittedContacts[params.data.contact_id];
      const submitted = survey !== undefined;
      const doNotContact = params.data.do_not_contact === true;
      return !submitted && !doNotContact ? styles.cursor : '';
    }

    getNodeInfo(data) {
      const { submittedContacts } = this.state;
      const submittedContact = submittedContacts[data.contact_id];
      const doNotContact = data.do_not_contact;
      const status = submittedContact ? submittedContact.contact_status : null;
      const isTerminated = status === 'TERMINATED';
      const valid = submittedContact === undefined && !doNotContact;
      const hasReason = submittedContact && submittedContact.terminated_reason && submittedContact.terminated_reason.length > 0;
      const terminatedReason = hasReason ? submittedContact.terminated_reason : null;
      const language = submittedContact ? submittedContact.language : null;
      const canResubmit = this.getCanResubmit();

      return {
        valid,
        isTerminated,
        doNotContact,
        submittedContact,
        status,
        hasReason,
        terminatedReason,
        language,
        canResubmit,
      };
    }

    getContextMenuItems(params) {
      const { node: { data } } = params;
      const {
        submittedContact, status, isTerminated,
        hasReason, language,
      } = this.getNodeInfo(data);
      const result = [];
      if (submittedContact) {
        if ((status === 'ACTIVE' || status === 'PAUSED') || (isTerminated && hasReason)) {
          result.push({
            name: isTerminated ? 'View Terminated Reason' : 'Cancel Outreach',
            action: () => {
              this.requestCancel({ ...data, ...submittedContact });
            },
            icon: '<i class="fa fa-ban text-danger"></i>',
          });
        }
        if ((status === 'ACTIVE' || status === 'PAUSED')) {
          const program = this.getSelectProgram();
          const defaultCollector = program ? program.defaultCollector : null;
          if (defaultCollector) {
            const availableLanguages = defaultCollector.used_languages || [];
            const hasMoreLanguages = difference(availableLanguages, [language]).length > 0;
            const id = this.props.isAccount ? this.props.object.account_id : this.props.object.opportunity_id;
            if (hasMoreLanguages) {
              result.push({
                name: 'Change Language',
                action: () => {
                  this.props.requestChangeLanguage({ ...data, ...submittedContact, object_id: id }, transalteLanguageCode(language), transalteLanguageCodes(availableLanguages), this.onLanguageChangeSuccess);
                },
                icon: '<i class="fa fa-language"></i>',
              });
            }
          }
        }
      }
      result.push('copy');
      return result;
    }

    getSelectProgram() {
      const { programs } = this.state;
      return find(programs, { selected: true });
    }

    getCanResubmit() {
      let canResubmit = false;
      const currentProgram = this.getSelectProgram();
      if (currentProgram && currentProgram.defaultCollector) {
        canResubmit = currentProgram.defaultCollector.can_accept_same_contact_submissions === true;
      }
      return canResubmit;
    }

    getAvailableLanguages() {
      const { collectorLanguageMappings, isAccount } = this.props;
      let availableLanguages = [];
      let language = null;
      const currentProgram = this.getSelectProgram();
      if (currentProgram && currentProgram.defaultCollector) {
        availableLanguages = currentProgram.defaultCollector.used_languages || [];
        ({ language } = currentProgram.defaultCollector);
        const type = isAccount ? 'experience' : 'decision';
        const userCollectorLanguage = find(collectorLanguageMappings, { collector_id: currentProgram.defaultCollector.collector_id, collector_type: type });
        if (userCollectorLanguage && userCollectorLanguage.language) {
          ({ language } = userCollectorLanguage);
        }
      }
      return { availableLanguages, language };
    }

    requestCancel(params) {
      const currentProgram = this.getSelectProgram();
      if (currentProgram && currentProgram.defaultCollector) {
        const collectorId = currentProgram.defaultCollector.collector_id;
        const { object } = this.props;
        this.props.requestCancelContact({ ...params, collector_id: collectorId }, object, this.onCancelSuccess);
      }
    }

    columnCellRendererSelector(params) {
      if (!params || !params.data) {
        return null;
      }
      const { currentSelectedContact, editedContacts, submittedContacts } = this.state;
      const survey = submittedContacts[params.data.contact_id];
      const submitted = survey !== undefined;
      const doNotContact = params.data.do_not_contact === true;
      const canResubmit = this.getCanResubmit();

      return {
        component: ContactDetailRenderer,
        params: {
          checkbox: !submitted && !doNotContact,
          currentSelectedContact,
          editedContacts,
          doNotContact,
          submitted,
          createdAt: survey ? survey.created_at : null,
          status: survey ? survey.contact_status : null,
          statusUpdatedDate: survey ? survey.last_contact_status_updated : null,
          canResubmit,
        },
      };
    }

    handleHeaderResize(w, headerHeight) {
      this.setState({ headerHeight });
    }

    handleFooterResize(w, footerHeight) {
      this.setState({ footerHeight });
    }

    handleSearch(query) {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(query);
      }
    }

    handleContactUpdate(contact) {
      const { editedContacts } = this.state;
      const index = findIndex(editedContacts, { _id: contact._id });
      if (index >= 0) {
        const newEditedContacts = [...editedContacts];
        newEditedContacts[index] = { ...contact };
        newEditedContacts[index].role = contact.role ? { ...contact.role } : {};
        this.setState({ editedContacts: newEditedContacts }, () => {
          this.gridApi.sizeColumnsToFit();
          this.checkValid();
        });
      }
    }

    handleRequestTerminate(contact, reason) {
      return new Promise((resolve, reject) => {
        const { object, isAccount } = this.props;
        const contactId = contact.id;
        const collectorId = contact.collector_id;
        const id = isAccount ? object.account_id : object.opportunity_id;
        this.props.dispatch(winLossCancelSurveys(collectorId, id, contactId, isAccount, reason))
          .then(
            (results) => {
              const { submittedContacts } = this.state;
              if (submittedContacts[contact.contact_id]) {
                const updatedContact = {
                  ...submittedContacts[contact.contact_id],
                  contact_status: 'TERMINATED',
                  terminated_reason: reason.reason,
                  last_contact_status_updated: moment(),
                  ...results,
                };

                this.setState({
                  submittedContacts: {
                    ...submittedContacts,
                    [updatedContact.contact_id]: updatedContact,
                  },
                  currentSelectedContact: updatedContact,
                }, () => {
                  if (this.gridApi) {
                    this.gridApi.refreshCells({ force: true });
                  }
                  resolve(results);
                });
              }
            },
            (e) => {
              reject(e);
            },
          );
      });
    }

    handleContactAdded(contact, error, cb = () => {}) {
      // display error attached to contact creation, such as failing to assign primary opportunity contact
      if (error) {
        this.context.addNotification((<div>{error.message || error}</div>), { type: 'error', autoClose: false });
      }
      
      this.props.onContactAdded(this.props.object, contact, this.props.isAccount);

      // flash row
      if (this.gridApi) {
        let rowIndex = null;
        this.gridApi.forEachNode((node) => {
          if (node.data && (node.data._id === contact._id || node.data.contact_id === contact.contact_id)) {
            ({ rowIndex } = node);
            this.onContactCellClicked({ node, data: node.data }, cb);
          }
        });
        if (rowIndex >= 0) {
          const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
          if (rowNode) {
            this.gridApi.flashCells({
              rowNodes: [rowNode],
            });
          }
        }
      }
    }

    handleSelectedOwner(selectedOwner, forceSenderEmailFrom = false) {
      this.setState({ selectedOwner, forceSenderEmailFrom }, () => {
        this.checkValid(() => {
          this.checkAutoSubmit('handleSelectedOwner');
        });
      });
    }

    handleProgramSelected(prog) {
      const requestedProgramId = prog ? prog.id : null;
      const currentProgram = find(this.state.programs, { selected: true });
      const programs = reduce(this.state.programs, (items, program) => {
        items.push({
          ...program,
          selected: program.id === requestedProgramId,
        });
        return items;
      }, []);
      const changeProgram = currentProgram && currentProgram.id !== requestedProgramId;
      const currentSelectedContact = changeProgram ? null : this.state.currentSelectedContact;
      const selectedOwner = changeProgram ? null : this.state.selectedOwner;
      this.setState({ programs, selectedOwner, currentSelectedContact }, () => {
        this.loadSurveys()
          .then(() => {
            this.checkValid(() => {
              this.checkAutoSubmit('handleProgramSelected');
            });
          });
      });
    }

    handleCollectorSelected(collector) {
      const currentProgram = find(this.state.programs, { selected: true });
      if (currentProgram) {
        const requestedProgramId = currentProgram ? currentProgram.id : null;
        const programs = reduce(this.state.programs, (items, program) => {
          let { defaultCollector } = program;
          if (requestedProgramId && requestedProgramId === program.id && collector && collector.collector_id) {
            defaultCollector = find(currentProgram.collectors, { collector_id: collector.collector_id, type: collector.type });
          }
          items.push({
            ...program,
            defaultCollector,
          });
          return items;
        }, []);
        this.setState({ programs }, () => {
          this.checkContacts();
          this.checkValid(() => {
            this.checkAutoSubmit('handleCollectorSelected');
          });
        });
      }
    }

    handleProductSelected(product) {
      const { allowMultipleProducts } = this.props;
      const currentProgram = find(this.state.programs, { selected: true });
      const requestedProductId = product ? product.id : null;
      const programs = reduce(this.state.programs, (items, program) => {
        let { productList } = program;
        if (currentProgram.id === program.id) {
          if (allowMultipleProducts) {
            productList = reduce(program.productList, (products, prod) => {
              products.push({
                ...prod,
                selected: prod.id === requestedProductId ? !prod.selected : prod.selected,
              });
              return products;
            }, []);
          } else {
            productList = reduce(program.productList, (products, prod) => {
              products.push({
                ...prod,
                selected: prod.id === requestedProductId,
              });
              return products;
            }, []);
          }
        }

        items.push({
          ...program,
          productList,
        });
        return items;
      }, []);
      this.setState({ programs }, () => {
        this.checkValid(() => {
          this.checkAutoSubmit('handleProductSelected');
        });
      });
    }

    handleSubmit(isOverride=false) {
      if (this.state.submitting) {
        return false;
      }
      this.setState({ submitting: true });
      const {
        selectedContacts, selectedOwner,
      } = this.state;
      const { object, isAccount, tvUseProductMappings } = this.props;
      const selectedProgram = this.getSelectProgram();
      if (!selectedProgram) {
        return false;
      }
      const requireProducts = isAccount || (!isAccount && this.props.requireProducts);

      let selectedProducts = filter(selectedProgram.productList, { selected: true });
      if (!tvUseProductMappings) {
        selectedProducts = [...selectedProgram.productList];
      }
      if (requireProducts && selectedProducts.length === 0) {
        return false;
      }

      this.setState({ resultContacts: [] });
      let sendFromEmail = null;
      // if we select a collector email, then set it to null
      // or else use the email we select
      if (selectedOwner && !selectedOwner.isCollector) {
        sendFromEmail = selectedOwner.id;
      }

      this.props.dispatch(submitContactsToSurveyCollector(
        selectedProgram, selectedProgram.defaultCollector, selectedProducts,
        sendFromEmail, object, selectedContacts, selectedProgram.defaultCollector.type,
        isAccount, isOverride === true, this.props.automationId, this.state.forceSenderEmailFrom, this.state.debug,
      ))
        .then(
          ({ contacts, feedbacks, surveys }) => {
            const resultContacts = [];
            const errorContacts = [];
            each(contacts, (contact) => {
              if (contact.success) {
                resultContacts.push(contact);
              } else {
                errorContacts.push(contact);
              }
            });
            if (resultContacts.length === 0 && errorContacts.length === 0) {
              // nothing was return for some reasons
              const message = 'No contact was submitted';
              this.context.addNotification((<div><p>{message}</p><p>Please try again.</p></div>), { type: 'error' });
              this.setState({ submitting: false });
            } else {
              this.props.onSuccess(object, feedbacks, surveys, resultContacts, errorContacts);
              this.setState({
                resultContacts,
                errorContacts,
              });
            }
          },
          (e) => {
            const message = e && e.data ? e.data.message || 'Unknown Error' : 'Unknown Error';
            this.context.addNotification((<div><p>{message}</p><p>Please try again.</p></div>), { type: 'error' });
            this.setState({ submitting: false });
          },
        );
      return true;
    }

    handleSellerFeedbackSuccess(success = false) {
      this.setState({ sellerFormSuccess: true }, () => {
        this.props.onSellerFeedbackSuccess();
        if (success) {
          this.context.addNotification((<div><p>Seller Feedback has been submitted successfully.</p></div>), { type: 'success' });
        }
      });
    }

    checkboxSelection({ data }) {
      if (!data) {
        return false;
      }
      const { submittedContacts } = this.state;
      const canResubmit = this.getCanResubmit();
      const valid = (canResubmit || submittedContacts[data.contact_id] === undefined) && !data.do_not_contact;
      return valid;
    }

    checkValid(cb = () => {}) {
      let valid = false;
      const selectedContacts = [];
      const {
        editedContacts, selectedOwner, submittedContacts, tvUseProductMappings,
      } = this.state;
      const requireProducts = this.props.isAccount || (!this.props.isAccount && this.props.requireProducts);
      const canResubmit = this.getCanResubmit();

      if (this.gridApi) {
        this.gridApi.forEachNode((node) => {
          if (node.selected && node.data) {
            const contact = find(editedContacts, { _id: node.data._id });
            if (contact && (submittedContacts[node.data.contact_id] === undefined || canResubmit)) {
              const ct = {
                ...contact,
                role: contact.role ? { ...contact.role } : null,
              };
              ct.$invalid = validateContact(ct) !== undefined;
              selectedContacts.push(ct);
            }
          }
        });
      }
      const validContacts = filter(selectedContacts, (sContact) => {
        return !sContact.$invalid;
      });
      const selectedProgram = this.getSelectProgram();
      let selectedProducts = selectedProgram ? filter(selectedProgram.productList, { selected: true }) : [];

      if (!tvUseProductMappings) {
        selectedProducts = selectedProgram ? [...selectedProgram.productList] : []; // select everything
      }

      const hasProduct = (selectedProgram && selectedProgram.defaultCollector && selectedProducts.length > 0);

      valid = (hasProduct || !requireProducts) && validContacts.length > 0 && selectedOwner && !selectedOwner.needVerification;
      this.setState({ valid, selectedContacts }, () => {
        cb();
      });
    }

    selectNodes(editedContacts) {
      const selectedContacts = [];
      if (this.gridApi) {
        this.gridApi.forEachNode((node) => {
          if (node.data) {
            const contact = find(editedContacts, { _id: node.data._id });
            if (contact) {
              selectedContacts.push({
                ...contact,
                role: contact.role ? { ...contact.role } : null,
              });
              node.setSelected(true);
            }
          }
        });
      }
      this.setState({ selectedContacts }, () => {
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true });
        }
      });
    }

    initData(props) {
      if (props.object) {
        this.loadContacts(props.object);
        const programs = initProductList(props);
        this.setState({ programs }, () => {
          this.loadSurveys()
            .then(() => {
              this.checkValid(() => {
                this.checkAutoSubmit('loadSurveys');
              });
            });
        });
      }
    }

    checkContacts() {
      const { surveys } = this.props;
      const currentProgram = find(this.state.programs, { selected: true });
      if (surveys && currentProgram && currentProgram.defaultCollector && currentProgram.defaultCollector.collector_id) {
        const submittedContacts = reduce(surveys, (items, survey) => {
          if (survey.collector_id === currentProgram.defaultCollector.collector_id) {
            items[survey.contact_id] = survey;
          }
          return items;
        }, {});
        return new Promise((resolve) => {
          this.setState({ submittedContacts }, resolve);
        });
      }
      return new Promise((resolve) => {
        this.setState({ submittedContacts: {} }, resolve());
      });
    }

    loadSurveys() {
      const { surveys, isAccount, object } = this.props;
      if (!surveys && this.mounted) {
        const id = isAccount ? object.account_id : object.opportunity_id;
        const fetchAction = isAccount ? fetchWinLossAccountSurveys : fetchWinLossOpportunitySurveys;
        return new Promise((resolve, reject) => {
          this.setState({ surveyLoading: true, surveyLoaded: false, surveyError: null, surveyErrorCode: null}, () => {
            this.props.dispatch(fetchAction(id))
              .then(
                () => {
                  this.checkContacts().then(() => {
                    this.setState({ surveyLoading: false }, resolve);
                  }, reject);
                },
                (surveyError) => {
                  const message = surveyError && surveyError.data && surveyError.data.message ? surveyError.data.message : 'Error loading survey data';
                  const surveyErrorCode = surveyError && surveyError.data && surveyError.data.code ? surveyError.data.code : null;
                  this.setState({
                    surveyLoading: false, surveyLoaded: false, surveyError: message, surveyErrorCode,
                  }, resolve);
                },
              );
          });
        });
      }
      if (surveys) {
        return new Promise((resolve) => {
          this.checkContacts()
            .then(
              () => {
                this.setState({ surveyLoading: false }, resolve);
              }
            );
        });
      }
      return Promise.resolve(true);
    }

    loadContacts(object) {
      this.setState({ loading: true, loaded: false });
      const { isAccount } = this.props;
      const fetchAction = isAccount ? fetchWinLossAccountContacts : fetchWinLossOpportunityContacts;
      const id = isAccount ? object.account_id : object.opportunity_id;
      this.props.dispatch(fetchAction(id))
        .then(
          () => {
            if (this.mounted) {
              this.setState({ loading: false, loaded: true }, () => {
                delay(() => {
                  if (this.gridApi) {
                    this.gridApi.sizeColumnsToFit();
                  }
                }, 500);
              });
            }
          },
          (errors) => {
            if (this.mounted) {
              const msg = errors && errors.data ? errors.data.message || 'Error loading contacts' : 'Error loading contacts';
              this.setState({ loading: false, loaded: true, errors: msg });
            }
          },
        );
    }

    reset() {
      this.setState({ ...initState });
    }

    close() {
      const { isAccount } = this.props;
      const action = isAccount ? requestEditWinLossAccountFeedbacks : requestEditWinLossOpportunityFeedbacks;
      this.props.dispatch(action(null));
      this.setState({ loading: false, loaded: false }, () => {
        delay(() => {
          this.reset();
        }, 500);
      });
    }

    closeWithNotification() {
      if (this.state.resultContacts && this.state.resultContacts.length) {
        this.props.onDone(this.props.object);
      }
      this.close();
    }

    valueGetter(params) {
      if (!params || !params.data) {
        return null;
      }
      const { editedContacts } = this.state;
      const contact = find(editedContacts, { _id: params.data._id }) || params.data;
      const names = [];
      if (contact.first_name && contact.first_name.length) {
        names.push(contact.first_name);
      }
      if (contact.last_name && contact.last_name.length) {
        names.push(contact.last_name);
      }
      return names.join(' ');
    }

    fixErrors() {
      const { errorContacts, selectedContacts } = this.state;
      const errorIds = reduce(errorContacts, (items, e) => {
        items.push(e.contact.contact_id);
        return items;
      }, []);
      const newSelectedContacts = reduce(selectedContacts, (items, contact) => {
        if (indexOf(errorIds, contact.contact_id) >= 0) {
          items.push(contact);
        }
        return items;
      }, []);
      if (this.gridApi) {
        this.gridApi.forEachNode((node) => {
          const nodeData = node.data;
          if (nodeData) {
            node.setSelected(indexOf(errorIds, nodeData.contact_id) >= 0);
          } else {
            node.setSelected(false);
          }
        });
        this.onSelectionChanged();
      }
      this.setState({
        errorContacts: [],
        selectedContacts: newSelectedContacts,
        currentSelectedContact: newSelectedContacts.length > 0 ? newSelectedContacts[0] : null,
        submitting: false,
      }, () => {
        this.gridApi.sizeColumnsToFit();
      });
    }

    handleResubmit() {
      this.setState({ showConfirmationModal: false });

      const { organization } = this.props;
      const { errorContacts, selectedContacts } = this.state;

      if (!organization?.feedback_request_settings?.collector_settings_editable) {
        return;
      }

      const errorIds = reduce(errorContacts, (items, e) => {
        items.push(e.contact.contact_id);
        return items;
      }, []);
      const newSelectedContacts = reduce(selectedContacts, (items, contact) => {
        if (indexOf(errorIds, contact.contact_id) >= 0) {
          items.push(contact);
        }
        return items;
      }, []);

      this.setState({
        errorContacts: [],
        selectedContacts: newSelectedContacts,
        currentSelectedContact: newSelectedContacts.length > 0 ? newSelectedContacts[0] : null,
        submitting: false,
      }, () => {
        this.handleSubmit(organization?.feedback_request_settings?.collector_settings_editable)
      });
    }

    renderAddContactButton(title = null) {
      const { isAccount, feedbackRequestSettings } = this.props;
      const enabled = feedbackRequestSettings.add_contacts === true && feedbackRequestSettings.can_add_contacts === true;
      if (!enabled) {
        return null;
      }
      const opportunity = isAccount ? null : this.props.object;
      const account = isAccount ? this.props.object : this.props.object.account;
      return (
        <AddContactButton
          opportunity={opportunity}
          account={account}
          isAccount={isAccount}
          title={(title || <Fa name="user" />)}
          onSuccess={this.handleContactAdded}
        />
      );
    }

    renderContactList(showing) {
      const { object } = this.props;
      const contacts = object ? object.contacts || [] : [];
      const columnDefs = [
        {
          headerName: 'Contact',
          suppressMenu: true,
          checkboxSelection: this.checkboxSelection,
          valueGetter: this.valueGetter,
          onCellClicked: this.onContactCellClicked,
          cellRendererSelector: this.columnCellRendererSelector,
          width: 250,
        },
        {
          field: 'type',
          headerName: 'Contact Type',
          suppressMenu: true,
          rowGroup: true,
          hide: true,
        },
      ];

      return (
        <div className={styles.contactList} style={{ display: showing ? 'block' : 'none' }}>
          <nav className="navbar navbar-default navbar-controls">
            <table className="table" style={{ margin: 0 }}>
              <tbody>
                <tr>
                  <td style={{ width: '99%' }}>
                    <SearchField onSearch={this.handleSearch} collapsible={false} />
                  </td>
                  <td>
                    {this.renderAddContactButton()}
                  </td>
                </tr>
              </tbody>
            </table>
          </nav>
          <div className={`ag-theme-alpine ${styles.contactTable}`} style={{ height: 410, width: '100%' }} >
            <AgGridReact
              reactNext
              columnDefs={columnDefs}
              rowData={contacts}
              headerHeight={0}
              onGridReady={this.onGridReady}
              animateRows
              rowSelection="multiple"
              suppressRowClickSelection
              rowDeselection
              onSelectionChanged={this.onSelectionChanged}
              getRowClass={this.getRowClass}
              groupDefaultExpanded={-1}
              getRowNodeId={data => data.contact_id}
              onRowSelected={this.onContactRowSelected}
              getContextMenuItems={this.getContextMenuItems}


              groupUseEntireRow
              groupDisplayType="groupRows"
              groupRowRendererParams={{
                innerRenderer: GroupRowInnerRenderer,
              }}

            />
          </div>
        </div>
      );
    }

    renderContactEditor(showing) {
      const {
        currentSelectedContact, submittedContacts,
        showingEditor,
      } = this.state;
      const { isMobile } = this.props;
      const submited = currentSelectedContact && currentSelectedContact.contact_id && submittedContacts[currentSelectedContact.contact_id] !== undefined;

      let availableLanguages = [];
      let language = null;

      if (showingEditor) {
        ({ availableLanguages, language } = this.getAvailableLanguages());
      }
      return (
        <div className={styles.contactEditor} style={{ display: showing ? 'block' : 'none' }}>
          {isMobile &&
            <div style={{ paddingBottom: 10 }}>
              <Button bsStyle="link" onClick={() => { this.setState({ showingEditor: false, currentSelectedContact: null }); }}><Fa name="chevron-left" /> Contact List</Button>
            </div>
          }
          <ContactEditor
            contact={currentSelectedContact}
            submited={submited}
            canResubmit={this.getCanResubmit()}
            availableLanguages={availableLanguages}
            language={language}
            onUpdated={this.handleContactUpdate}
            requestCancel={this.handleRequestTerminate}
          />
        </div>
      );
    }

    renderEmailSelector(opportunityOwnerSignatureVerified, isOwner, owners, collectorSignatures, dynamicSenders,authSignature, wrapper = false) {
      const { object, isAccount } = this.props;
      const selectedProgram = this.getSelectProgram();
      const collector = selectedProgram ? selectedProgram.defaultCollector : null;
      const inner = (
        <CheckUserEmailSignature collectorId={collector?.collector_id}>
          {({ verified, loaded }) => {
            if (!loaded) {
              return null;
            }
            return (
              <div>
                <OwnerSelector
                  verified={opportunityOwnerSignatureVerified}
                  authEmailVerified={verified}
                  isOwner={isOwner}
                  isAccount={isAccount}
                  owners={owners}
                  authSignature={authSignature}
                  dynamicSenders={dynamicSenders}
                  collectorSignatures={collectorSignatures}
                  selectedOwner={this.state.selectedOwner}
                  onSelect={this.handleSelectedOwner}
                  collector={collector}
                  sfOwner={object ? object.owner : null}
                />
              </div>
            );
          }}
        </CheckUserEmailSignature>
      );
      if (!wrapper) {
        return inner;
      }
      return (
        <div className={styles.steps}>
          {inner}
        </div>
      );
    }

    renderExcludeBtn() {
      if (this.props.isAccount) {
        return null;
      }
      return (
        <Button bsStyle="default" onClick={() => { this.props.requestExclude(this.props.object); }}>Exclude Opportunity</Button>
      );
    }

    renderSubmitForm(show, verified, isOwner, owners, collectorSignatures, dynamicSenders, authSignature) {
      const {
        programs,
        valid,
      } = this.state;
      const { isAccount, isModal, requestExclude, object, withPreferCollector, feedbackRequestSettings, allowMultipleProducts } = this.props;

      const selectedProgram = this.getSelectProgram();
      const foundProduct = programs.length === 1 && selectedProgram && selectedProgram.hasAtLeastOneMappedProduct && selectedProgram.collectors.length === 1;

      let btn = (
        <Button bsStyle="primary" disabled={!valid} onClick={() => {
          this.handleSubmit(false);
        }}>Submit</Button>
      );
      if (!isAccount && !isModal && !valid) {
        btn = (
          <Button onClick={this.props.onDone} bsStyle="default">Done</Button>
        );
      }
      let requireProducts = true;
      if (!isAccount) {
        ({ requireProducts } = this.props);
      }

      return (
        <div style={{ display: show ? 'block' : 'none' }}>
          {(!foundProduct || !allowMultipleProducts) &&
            <InitiativeSelector
              programs={programs}
              onProgramSelect={this.handleProgramSelected}
              onCollectorSelect={this.handleCollectorSelected}
              onProductSelect={this.handleProductSelected}
              allowMultipleProducts={allowMultipleProducts}
              requireProducts={requireProducts}
              key={selectedProgram?.defaultCollector.collectorId}
              withPreferCollector={withPreferCollector && feedbackRequestSettings.collector_id ? feedbackRequestSettings.collector_id : null}
            >
              {this.renderEmailSelector(verified, isOwner, owners, collectorSignatures, dynamicSenders, authSignature)}
            </InitiativeSelector>
          }
          {(foundProduct && allowMultipleProducts) && this.renderEmailSelector(verified, isOwner, owners, collectorSignatures, dynamicSenders, authSignature, true)}
          <hr />
          <div className="clearfix">
            <div className="pull-left">
              {requestExclude !== null && !object.excluded && this.renderExcludeBtn()}
            </div>
            <div className="pull-right text-right">
              {btn}
            </div>
          </div>
        </div>
      );
    }

    renderContent(show, verified, isOwner, owners, collectorSignatures, dynamicSenders, authSignature) {
      const { showingEditor } = this.state;
      const { isMobile, object, isAccount, feedbackRequestSettings } = this.props;
      const shouldShowList = !isMobile || (isMobile && !showingEditor);
      const shouldShowEditor = !isMobile || (isMobile && showingEditor);
      const contacts = object ? object.contacts || [] : [];
      const objLabel = isAccount ? 'account' : 'opportunity';
      const { instructions } = feedbackRequestSettings; // HTML

      if (contacts.length === 0) {
        return (
          <div>
            <div className="alert alert-danger">
              <p>No contacts found for this {objLabel}.</p>
            </div>
            <p>{this.renderAddContactButton(<span><Fa name="user" /> Add a New Contact</span>)}</p>
          </div>
        );
      }
      return (
        <div style={{ display: show ? 'block' : 'none' }} >
          <div className="well well-sm" dangerouslySetInnerHTML={{__html: instructions }} />

          <div className={isMobile ? null : styles.columns}>
            {this.renderContactList(shouldShowList)}
            {this.renderContactEditor(shouldShowEditor)}
          </div>
          {this.renderSubmitForm(shouldShowList, verified, isOwner, owners, collectorSignatures, dynamicSenders, authSignature)}
        </div>
      );
    }

    renderErrors(errors) {
      const { object } = this.props;
      return (
        <div className="alert alert-danger">
          Found <NumberDisplay value={errors.length} precision={0} suffix="error" suffixIsUnit />:
          <ul>
            {errors.map((e) => {
              const isDuplicated = e.errors.message === 'Duplicated contact.';
              const name = `${e.contact.first_name} ${e.contact.last_name}`;
              return (
                <li key={e.contact.contact_id}>
                  {isDuplicated &&
                    <div><strong>{name}</strong> was not submitted. They have been asked to provide feedback for a past opportunity and campaign is set to not allow additional submissions.</div>
                  }
                  {!isDuplicated &&
                    <div>{name}: {e.errors.message}</div>
                  }
                </li>
              );
            })}
          </ul>
        </div>
      );
    }

    renderSuccess(resultContacts) {
      const { object } = this.props;
      const have = resultContacts.length === 1 ? 'has' : 'have';

      return (
        <div className="alert alert-success">
          <NumberDisplay value={resultContacts.length} precision={0} suffix="contact" suffixIsUnit /> {have} been added!
          <ul>
            {resultContacts.map((e) => {
              const name = `${e.contact.first_name} ${e.contact.last_name}`;
              return (
                <li key={e.contact.contact_id}>
                  <strong>{name}</strong> has been added to the feedback request campaign for <strong>{object.name}</strong>
                </li>
              );
            })}
          </ul>
        </div>
      );
    }

    renderSummary() {
      const { object, isAccount } = this.props;
      const selectedProgram = this.getSelectProgram();
      if (!object || !selectedProgram || !selectedProgram.defaultCollector) {
        return null;
      }

      return (<CollectorStepSummary collector={selectedProgram.defaultCollector} isAccount={isAccount} object={object} />);
    }

    buildMessage() {
      const { errorContacts, selectedContacts } = this.state;
      const errorIds = reduce(errorContacts, (items, e) => {
        items.push(e.contact.contact_id);
        return items;
      }, []);
      const newSelectedContacts = reduce(selectedContacts, (items, contact) => {
        if (indexOf(errorIds, contact.contact_id) >= 0) {
          items.push(contact);
        }
        return items;
      }, []);
      return (
        <div>
          <p>
            Are you sure you want to resubmit the follwing contacts?<br/>
            <b>This process ignores collector settings regarding submission restrictions.</b>
          </p>
          <p>
          <ul style={{ paddingTop: '15px', paddingBottom: '15px', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>
            {newSelectedContacts.map((c) => {
              const name = `${c.first_name} ${c.last_name}`;
              return (
                <li key={c.contact_id}>{name}</li>
              );
            })}
          </ul>
          </p>
        </div>
      )
    }

    renderResult() {
      const { resultContacts, errorContacts } = this.state;
      const { isModal, withSummary, organization } = this.props;
      const hasErrors = errorContacts.length > 0;
      return [
        (
          <div key="body">
            {!hasErrors && this.renderSuccess(resultContacts)}
            {hasErrors > 0 &&
              <div>
                {resultContacts.length > 0 && this.renderSuccess(resultContacts)}
                {this.renderErrors(errorContacts)}
              </div>
            }
          </div>
        ),
        [...(withSummary ? [(
          <div key="summary">
            <p />
            {this.renderSummary()}
            <p />
          </div>
        )] : [])],
        (
          <div key="footer">
            <div className="clearfix">
              {hasErrors &&
                <Button className="pull-left" onClick={this.fixErrors} bsStyle="link">
                  <span className="text-danger"><Fa name="chevron-left" /> Fix Errors</span>
                </Button>
              }
              {isModal &&
                <Button className="pull-right" onClick={this.closeWithNotification} bsStyle="primary">Done</Button>
              }
              {!isModal &&
                <Button className="pull-right" onClick={this.props.onDone} bsStyle="primary">Done</Button>
              }
              {hasErrors && organization?.feedback_request_settings?.collector_settings_editable && (
                <div>
                  <ConfirmationModal
                    show={this.state.showConfirmationModal}
                    message={this.buildMessage()}
                    onSubmit={this.handleResubmit}
                    onCancel={() => this.setState({ showConfirmationModal: false })}
                    handleClose={() => this.setState({ showConfirmationModal: false })}
                  />
                  <Button
                    className={styles.outlineDanger}
                    onClick={() => this.setState({ showConfirmationModal: true })}
                    bsStyle="link">
                    Override & Resubmit
                  </Button>
                </div>
              )}
            </div>
          </div>
        ),
      ];
    }

    renderSubmitting() {
      const {
        resultContacts, errorContacts, sellerFormSuccess,
        headerHeight, footerHeight,
      } = this.state;
      const {
        shouldRenderSellerFormLater, object, collectorTypes,
        viewableDimensions: { height },
        isAccount,
      } = this.props;

      const remainingHeight = Math.max(0, height - headerHeight - footerHeight - 60); // margin: 30

      const showSellerForm = shouldRenderSellerFormLater && !isAccount;

      const hasResults = resultContacts.length > 0 || errorContacts.length > 0;

      return (
        <div>
          {hasResults &&
            <div>
              {!showSellerForm && this.renderResult()}
              {showSellerForm &&
                <div>
                  <SellerFeedbackForm
                    opportunity={object}
                    collectorTypes={collectorTypes}
                    onSuccess={this.handleSellerFeedbackSuccess}
                    maxHeight={remainingHeight}
                  />
                  {sellerFormSuccess &&
                    <div className="clearfix">
                      <p />
                      <div className="pull-right text-right">
                        <Button byStyle="primary" onClick={() => { this.props.onSellerFeedbackSuccess(); }}>Continue <Fa name="angle-double-right" /></Button>
                      </div>
                      <ReactResizeDetector handleHeight onResize={this.handleFooterResize} />
                    </div>
                  }
                </div>
              }
            </div>
          }
          {!hasResults &&
            <div className="text-center">
              <ProgressBar />
              <p />
              <p>one moment please...</p>
            </div>
          }
        </div>
      );
    }

    renderSurveyError() {
      const { surveyError, surveyErrorCode } = this.state;
      if (surveyErrorCode === 'PERMISSION_ERROR') {
        return (
          <div>
            <div className="alert alert-danger">
              <p>Uh oh! It looks like you do not have permission to access opportunities you are not the owner of. This is because your TruVoice Automation role only allows you to access and submit feedback requests for opportunities you own.</p>
            </div>
            <p />
            <p>If you need support you can contact one of your TruVoice Automation administrators for additional help.</p>
            <ManagerList />
            <p />
            <p>Or, contact TruVoice by Corporate Visions for support at <a href="mailto:support@truvoice.io">support@truvoice.io</a>.</p>
          </div>
        );
      }
      return (
        <div className="alert alert-danger">
          {surveyError}
        </div>
      );
    }

    renderTitle() {
      const {
        object, title,
      } = this.props;
      if (title) {
        return title;
      }
      const opportunityName = object ? object.name : null;
      return (
        <span>Request Feedback For {opportunityName}</span>
      );
    }

    renderBuyerFeedbackForm() {
      const {
        isModal, object, isAccount,
      } = this.props;
      const {
        loading, loaded, submitting,
        selectedOwner, errors, surveyLoading, surveyError,
      } = this.state;
      const isSurveyLoading = surveyLoading;
      const isLoading = loading || !loaded || !object;
      const selectedProgram = this.getSelectProgram();
      let contents = null;
      const hasErrors = !loading && !loaded && errors !== null;
      const hasSurveyError = !isSurveyLoading && surveyError !== null;

      if (selectedOwner && selectedOwner.needVerification) {
        contents = (
          <RequiredUserEmailSignature
            onVerified={() => { this.setState({ selectedOwner: { ...this.state.selectedOwner, needVerification: false }, currentSelectedContact: null }); }}
            onCancel={() => { this.setState({ selectedOwner: null, currentSelectedContact: null }); }}
            collectorId={selectedProgram?.defaultCollector?.collector_id}
          >
            <div />
          </RequiredUserEmailSignature>
        );
      } else {
        contents = (
          <RequiredSignature
            initiative={selectedProgram}
            object={object}
            isAccount={isAccount}
            key={selectedProgram?.defaultCollector?.collector_id}
            onCancel={() => {
              this.handleProgramSelected(null);
            }}
          >
            {({ isOwner, verified, owners, collectorSignatures, authSignature, dynamicSenders }) => (
              <div>
                <div>
                  <Modal.Header closeButton={isModal}>
                    <Modal.Title>{this.renderTitle()}</Modal.Title>
                  </Modal.Header>
                  <ReactResizeDetector handleHeight onResize={this.handleHeaderResize} />
                </div>
                <Modal.Body>
                  {isLoading && <div><p>Loading account contacts...</p><ProgressBar /></div>}
                  {!isLoading && !hasErrors &&
                    <div>
                      {isSurveyLoading && <div><p>Loading survey detail...</p><ProgressBar /></div>}
                      {!isSurveyLoading && !hasSurveyError &&
                        <div>
                          {this.renderContent(!submitting, verified, isOwner, owners, collectorSignatures, dynamicSenders, authSignature)}
                          {submitting && this.renderSubmitting()}
                        </div>
                      }
                      {!isSurveyLoading && hasSurveyError && this.renderSurveyError()}
                    </div>
                  }
                  {!isLoading && hasErrors &&
                    <div className="alert alert-danger">
                      {errors}
                    </div>
                  }
                </Modal.Body>
              </div>
            )}
          </RequiredSignature>
        );
      }
      return contents;
    }

    renderSellerFeedbackForm() {
      const {
        object, collectorTypes, isModal,
        viewableDimensions: { height },
      } = this.props;
      const { headerHeight, footerHeight, sellerFormSuccess } = this.state;

      const remainingHeight = Math.max(0, height - headerHeight - footerHeight - 60); // margin: 30

      return (
        <div>
          <div>
            <Modal.Header closeButton={isModal}>
              <Modal.Title>{this.renderTitle()}</Modal.Title>
            </Modal.Header>
            <ReactResizeDetector handleHeight onResize={this.handleHeaderResize} />
          </div>
          <Modal.Body>
            <SellerFeedbackForm
              opportunity={object}
              collectorTypes={collectorTypes}
              onSuccess={this.handleSellerFeedbackSuccess}
              maxHeight={remainingHeight}
            />
          </Modal.Body>
          {sellerFormSuccess &&
            <div>
              <Modal.Footer closeButton={isModal}>
                <Button byStyle="primary" onClick={() => { this.props.onSellerFeedbackSuccess(); }}>Continue <Fa name="angle-double-right" /></Button>
              </Modal.Footer>
              <ReactResizeDetector handleHeight onResize={this.handleFooterResize} />
            </div>
          }
        </div>
      );
    }

    renderDisabledFeedbackForm() {
      const { isModal, isAccount } = this.props;
      return (
        <div>
          <Modal.Header closeButton={isModal}>
            <Modal.Title>{this.renderTitle()}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-danger">
              Feedback requests are disabled for this {isAccount ? 'account' : 'opportunity'}.
            </div>
          </Modal.Body>
        </div>
      )
    }

    render() {
      const {
        show, isModal, mode,
        feedbackRequestable,
      } = this.props;
      const { debug } = this.state;
      let contents = null;
      if (mode === MODE_SELLER) {
        contents = this.renderSellerFeedbackForm();
      } else {
        if (feedbackRequestable) {
          contents = this.renderBuyerFeedbackForm();
        } else {
          contents = this.renderDisabledFeedbackForm();
        }
      }

      const debugComp = debug ? (
        <div className="alert alert-warning">Debug mode enabled</div>
      ) : null;

      if (isModal) {
        return (
          <Modal
            show={show}
            onHide={this.close}
            keyboard={false}
            bsSize="lg"
          >
            {debugComp}
            {contents}
            <Konami easterEgg={this.toggleDebug} />
          </Modal>
        );
      }
      return (
        <div className={styles.standalone}>
          {debugComp}
          {contents}
          <Konami easterEgg={this.toggleDebug} />
        </div>
      );
    }
  }
  RequestOpportunityFeedbacks.propTypes = {
    dispatch: PropTypes.func.isRequired,
    object: PropTypes.shape({
      _id: PropTypes.string,
      opportunity_id: PropTypes.string,
      account_id: PropTypes.string,
      owner_id: PropTypes.string,
      name: PropTypes.string,
      account: PropTypes.shape({}),
      has_seller_feedback: PropTypes.bool,
      contact: PropTypes.shape({}),
    }),
    show: PropTypes.bool,
    onClose: PropTypes.func,
    onSuccess: PropTypes.func,
    tvClients: PropTypes.arrayOf(PropTypes.shape({})),
    tvCollectors: PropTypes.arrayOf(PropTypes.shape({})),
    collectors: PropTypes.shape({}),
    tvProductMappings: PropTypes.shape({}),
    onDone: PropTypes.func,
    isModal: PropTypes.bool,
    isMobile: PropTypes.bool,
    withSummary: PropTypes.bool,
    withPreferCollector: PropTypes.bool,
    collectorTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    isAccount: PropTypes.bool,
    surveys: PropTypes.arrayOf(PropTypes.shape({})),
    organization: PropTypes.shape({
      seller_reasons_first: PropTypes.bool,
      seller_reasons_public: PropTypes.bool,
      seller_reasons_enabled: PropTypes.bool,
    }).isRequired,
    tvUseProductMappings: PropTypes.bool,
    mode: PropTypes.oneOf([MODE_BUYER, MODE_SELLER]),
    shouldRenderSellerFormLater: PropTypes.bool,
    viewableDimensions: PropTypes.shape({
      height: PropTypes.number,
    }),
    onSellerFeedbackSuccess: PropTypes.func,
    feedbackRequestSettings: PropTypes.shape({
      add_contacts: PropTypes.bool,
      can_add_contacts: PropTypes.bool,
      can_make_contact_primary: PropTypes.bool,
      instructions: PropTypes.string,
      collector_id: PropTypes.string,
    }),
    title: PropTypes.node,
    requestCancelContact: PropTypes.func,
    requestChangeLanguage: PropTypes.func,
    requestExclude: PropTypes.func,
    collectorLanguageMappings: PropTypes.arrayOf(PropTypes.shape({})),
    requireProducts: PropTypes.bool,
    allowMultipleProducts: PropTypes.bool,
    automationId: PropTypes.string,
    feedbackRequestable: PropTypes.bool,
    onContactAdded: PropTypes.func,
  };
  RequestOpportunityFeedbacks.defaultProps = {
    onClose: () => {},
    onSuccess: () => {},
    show: false,
    object: null,
    tvClients: [],
    tvCollectors: [],
    collectors: {},
    tvProductMappings: {},
    onDone: () => {},
    isModal: true,
    isMobile: true,
    withSummary: true,
    withPreferCollector: false,
    tvUseProductMappings: true,
    isAccount: componentType === ACCOUNT_COMPONENT_TYPE,
    surveys: null,
    mode: MODE_BUYER,
    shouldRenderSellerFormLater: false,
    viewableDimensions: {
      height: 0,
    },
    onSellerFeedbackSuccess: () => {},
    requestCancelContact: () => {},
    requestChangeLanguage: () => {},
    feedbackRequestSettings: {
      add_contacts: false,
      can_make_contact_primary: false,
      instructions: null,
      collector_id: null,
    },
    title: null,
    requestExclude: null,
    collectorLanguageMappings: [],
    requireProducts: true,
    allowMultipleProducts: true,
    automationId: null,
    feedbackRequestable: false,
    onContactAdded: () => {},
  };
  // Retrieve data from store as props
  function mapStateToProps(state) {
    const isAccount = componentType === ACCOUNT_COMPONENT_TYPE;
    const object = isAccount ? getWinLossEditingAccount(state) : getWinLossEditingOpportunity(state);
    const auth = getUser(state);

    const tvProductMappings = getTVProductDictionary(state);
    const tvUseProductMappings = getTVUseProductMappings(state);

    let surveys = null;
    if (object) {
      const id = isAccount ? object.account_id : object.opportunity_id;
      surveys = getWinLossOpportunitySurveys(state, id);
       }

    const organization = getUserCurrentOrganization(state);

    let mode = MODE_BUYER;
    let shouldRenderSellerFormLater = false;

    if (object) {
      if (organization.seller_reasons_enabled && !object.has_seller_feedback) {
        if (organization.seller_reasons_public || object.owner_id === auth.user_id) {
          if (organization.seller_reasons_first) {
            mode = MODE_SELLER;
          } else {
            shouldRenderSellerFormLater = true;
          }
        }
      }
    }

    const feedbackRequestableStatus = object ? getFeedbackRequestStatus(state, isAccount ? object.account_id : object.opportunity_id) : null;

    const feedbackRequestable = feedbackRequestableStatus && feedbackRequestableStatus.feedbackRequestable === true;

    return {
      auth,
      object,
      show: object !== null,
      tvClients: getTVProductClients(state),
      tvCollectors: getTVCollectorMappings(state),
      collectors: getTVCollectorRefs(state),
      tvProductMappings,
      isMobile: getIsMobile(state),
      isAccount,
      surveys,
      organization: getUserCurrentOrganization(state),
      mode,
      tvUseProductMappings,
      shouldRenderSellerFormLater,
      viewableDimensions: getViewableDimensions(state),
      feedbackRequestSettings: getFeedbackRequestSettings(state),
      collectorLanguageMappings: getCollectorLanguageMappings(state),
      requireProducts: getTVRequireProducts(state),
      allowMultipleProducts: getTVAllowMultipleProducts(state),
      feedbackRequestable,
    };
  }
  RequestOpportunityFeedbacks.contextTypes = {
    addNotification: PropTypes.func.isRequired,
  };
  return connect(mapStateToProps)(RequestOpportunityFeedbacks);
}